/**
 * Initializes mobile keyboard event detection and handling for iOS and other browsers.
 *
 * This function listens for viewport changes to detect when the virtual keyboard opens or closes,
 * dispatches custom events (`keyboardDidShow` and `keyboardDidHide`), and toggles visibility of elements
 * with the `.hide-on-keybord.fade.show` class accordingly.
 *
 * - Uses `window.visualViewport` for modern browsers, falls back to `window.resize` for older ones.
 * - Exposes a global `window.KeyboardHelper.isKeyboardOpen()` to check keyboard state.
 * - On keyboard show/hide, updates DOM elements and triggers a resize event.
 *
 * @function
 * @global
 *
 * @fires keyboardDidShow - Dispatched when the keyboard is detected as opened.
 * @fires keyboardDidHide - Dispatched when the keyboard is detected as closed.
 *
 * @example
 * initMobileKeybordEvent();
 * window.addEventListener('keyboardDidShow', () => { /* handle keyboard open });
 * window.addEventListener('keyboardDidHide', () => { /* handle keyboard close });
 **/
function initMobileKeybordEvent() {

    let lastHeight = window.visualViewport?.height || window.innerHeight;
    let keyboardOpen = false;

    function checkKeyboardState() {
        const currentHeight = window.visualViewport?.height || window.innerHeight;
        const heightDiff = lastHeight - currentHeight;

        if (Math.abs(heightDiff) < 50) return;

        // iOS keyboard typically reduces viewport height by >150px
        if (heightDiff > 150 && !keyboardOpen) {
            keyboardOpen = true;
            window.dispatchEvent(new CustomEvent('keyboardDidShow'));
        } else if (heightDiff < -100 && keyboardOpen) {
            keyboardOpen = false;
            window.dispatchEvent(new CustomEvent('keyboardDidHide'));
        }

        lastHeight = currentHeight;
    }

    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', checkKeyboardState);
        window.visualViewport.addEventListener('scroll', checkKeyboardState);
    } else {
        // Fallback for older browsers
        window.addEventListener('resize', checkKeyboardState);
    }

    // Optional global access
    window.KeyboardHelper = {
        isKeyboardOpen: () => keyboardOpen
    };

    window.addEventListener('keyboardDidShow', () => {
        console.log('Keyboard opened on iOS');
        document.querySelectorAll('.hide-on-keybord.fade.show').forEach(el => {
            el.classList.remove('show');
        });
    });

    window.addEventListener('keyboardDidHide', () => {
        console.log('Keyboard closed on iOS');
        document.querySelectorAll('.hide-on-keybord.fade').forEach(el => {
            el.classList.add('show');
            window.dispatchEvent(new Event('resize'));
        });
    });

}

module.exports = {
    init: initMobileKeybordEvent
};
